import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ManageSubscription } from "@/components/ManageSubscription";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Crown, Check, Sparkles, Leaf, CalendarRange, Scale, Infinity as InfinityIcon, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { trialDaysLeft } from "@/components/Upgrade";
import { formatDate } from "@/lib/plant";

const PREMIUM_FEATURES = [
  { icon: InfinityIcon, text: "Unlimited sprouts (Free is capped at 3)" },
  { icon: Sparkles, text: "AI Plant Doctor — photo health diagnosis" },
  { icon: Leaf, text: "AI Identify & AI Care Assistant" },
  { icon: CalendarRange, text: "Planting Calendar with sow & transplant dates" },
  { icon: Scale, text: "Harvest analytics across seasons" },
];

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    api.get("/billing/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ enabled: false }));
  }, []);

  const isPremium = user?.is_premium;
  const status = user?.subscription?.status;
  const sym = config?.symbol || "£";
  const subSym = { GBP: "£", USD: "$", EUR: "€" }[user?.subscription?.currency] || sym;
  const isCanceled = status === "canceled";
  const isPaid = status === "active" || isCanceled || user?.role === "admin";
  const isTrialing = user?.subscription?.status === "trialing";
  const planId = config?.enabled ? (cycle === "monthly" ? config.monthly_plan_id : config.yearly_plan_id) : null;

  const onApprove = async (data) => {
    setActivating(true);
    try {
      await api.post("/billing/activate", { subscription_id: data.subscriptionID, plan: cycle });
      await refreshUser();
      toast.success("Welcome to Sprout Pro! 🌟");
      navigate("/");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not activate subscription");
    } finally {
      setActivating(false);
    }
  };

  // PayPal returns here after a plan-switch approval: close the popup, or confirm if we were redirected.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const sw = params.get("switch");
    if (!sw) return;
    if (window.opener && window.name === "paypal-switch") { window.close(); return; }
    setParams({}, { replace: true });
    if (sw === "done") {
      api.post("/billing/switch/confirm").then(async ({ data }) => {
        await refreshUser();
        data.switched ? toast.success("Plan switched — new price starts at your next billing date") : toast.info("Plan switch is pending PayPal confirmation");
      }).catch(() => toast.error("Could not confirm the switch"));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-amber-400 to-orange-500 text-white mb-3">
            <Crown className="h-3.5 w-3.5" /> SPROUT PRO
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Grow without limits</h1>
          <p className="text-muted-foreground mt-2">Unlock AI, unlimited sprouts, planting calendar and harvest analytics.</p>
        </div>

        {isPaid ? (
          <div className="bg-card rounded-2xl border border-border p-8 text-center shadow-sm" data-testid="already-premium">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-3">
              <Crown className="h-7 w-7 text-white" />
            </div>
            <h2 className="font-display text-2xl font-bold">You're a Pro 🌟</h2>
            {user?.role === "admin" ? (
              <p className="text-muted-foreground mt-1">Admin account — all premium features unlocked.</p>
            ) : (
              <>
                {isCanceled ? (
                  <div className="mt-3 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-sm font-semibold" data-testid="subscription-status">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    Pro until {formatDate(user.subscription.current_period_end)} <span className="font-normal">· won't renew</span>
                  </div>
                ) : (
                  <div className="mt-3 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 text-sm font-semibold" data-testid="subscription-status">
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                    Pro active
                    {user?.subscription?.current_period_end && (
                      <span className="font-normal">· renews on {formatDate(user.subscription.current_period_end)}</span>
                    )}
                  </div>
                )}
                {user?.subscription?.plan && (
                  <p className="text-xs text-muted-foreground mt-2">
                    <span className="capitalize">{user.subscription.plan}</span> plan · {subSym}
                    {user.subscription.plan === "yearly" ? (config?.prices?.yearly || "49.99") + "/yr" : (config?.prices?.monthly || "4.99") + "/mo"}
                  </p>
                )}
              </>
            )}
            {user?.role !== "admin" && status === "active" && <ManageSubscription user={user} config={config} />}
            {isCanceled && (
              <p className="text-xs text-muted-foreground mt-4" data-testid="canceled-note">Your subscription is cancelled and you won't be charged again. Subscribe again any time after {formatDate(user.subscription.current_period_end)} to keep Pro.</p>
            )}
          </div>
        ) : (
          <>
            {isTrialing && (
              <div className="mb-6 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-2xl p-5 flex items-center gap-3 shadow-sm" data-testid="trial-banner">
                <Clock className="h-6 w-6 shrink-0" />
                <div>
                  <p className="font-semibold">Your free Pro trial is active — {trialDaysLeft(user)} day{trialDaysLeft(user) === 1 ? "" : "s"} left</p>
                  <p className="text-white/85 text-sm">Subscribe now to keep AI, unlimited sprouts, calendar &amp; harvest analytics when it ends.</p>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-5 gap-6">
            <div className="md:col-span-3 bg-card rounded-2xl border border-border p-6 shadow-sm">
              <h3 className="font-display font-semibold text-lg mb-4">Everything in Pro</h3>
              <ul className="space-y-3">
                {PREMIUM_FEATURES.map((f) => {
                  const Icon = f.icon;
                  return (
                    <li key={f.text} className="flex items-start gap-3">
                      <span className="h-7 w-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0">
                        <Icon className="h-4 w-4 text-emerald-600" />
                      </span>
                      <span className="text-sm pt-0.5">{f.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="md:col-span-2 bg-card rounded-2xl border border-border p-6 shadow-sm flex flex-col">
              <div className="flex rounded-xl bg-secondary p-1 mb-5" data-testid="cycle-toggle">
                <button onClick={() => setCycle("monthly")} className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition ${cycle === "monthly" ? "bg-card shadow-sm" : "text-muted-foreground"}`} data-testid="cycle-monthly">
                  Monthly
                </button>
                <button onClick={() => setCycle("yearly")} className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition ${cycle === "yearly" ? "bg-card shadow-sm" : "text-muted-foreground"}`} data-testid="cycle-yearly">
                  Yearly
                </button>
              </div>
              <div className="text-center mb-5">
                <div className="font-display text-4xl font-bold">
                  {sym}{cycle === "monthly" ? config?.prices?.monthly || "4.99" : config?.prices?.yearly || "49.99"}
                </div>
                <p className="text-sm text-muted-foreground">per {cycle === "monthly" ? "month" : "year"}{cycle === "yearly" && <span className="text-emerald-600 font-medium"> · save 17%</span>}</p>
              </div>

              {activating && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Activating…
                </div>
              )}

              {!config ? (
                <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : config.enabled && planId ? (
                <div data-testid="paypal-buttons">
                  <PayPalScriptProvider options={{ clientId: config.client_id, vault: true, intent: "subscription", currency: config.currency }}>
                    <PayPalButtons
                      key={cycle}
                      style={{ layout: "vertical", shape: "pill", label: "subscribe" }}
                      createSubscription={(data, actions) => actions.subscription.create({ plan_id: planId })}
                      onApprove={onApprove}
                      onError={() => toast.error("PayPal error — please try again")}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <div className="text-center text-sm text-muted-foreground bg-secondary/50 rounded-xl p-4" data-testid="paypal-unconfigured">
                  Online payments aren't switched on yet. The owner is finishing PayPal setup — check back shortly.
                </div>
              )}
            </div>
          </div>
          </>
        )}
      </div>
    </Layout>
  );
}
